import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { ConnectorProfile, ConnectorRole, type ConnectorDefinition } from '../../connectors/ConnectorDefinition';
import {
  BACKDROP_SOCKET,
  COLLECTIBLE_FLOAT_HEIGHT,
  CRANE_SOCKET,
  SITE_LAYOUT,
  backdropSocket,
  collectibleLanes,
  craneSocket,
  entryConnector,
  nearSideSockets,
  obstacleRow,
  standardLanes,
  type ModuleSides,
} from '../segmentBuilders';
import { arcExitPose, arcRadius, outerChordInset } from '../segmentPath';
import { SegmentKind, type DecorationSocket, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_15 / SEG_16 – smooth curves without input (design/01_segments/SEG_15_Curve_Left,
 * SEG_16_Curve_Right). The centre line is a circular arc; lanes keep their lateral offset,
 * so the runner simply follows the curve and may still change lanes. All socket data below is
 * given in path coordinates (x lateral, z = meters along the arc) – see segmentPath.ts.
 * Direction follows the sheet titles (the mini-maps show the opposite turn).
 *
 *   SEG_CURVE_{LEFT,RIGHT}_45_40M   40 m, 45°, radius 50.93 m  (sheet "Standard 40 m" / "weite Kurve")
 *   SEG_CURVE_{LEFT,RIGHT}_90_60M   60 m, 90°, radius 38.20 m  (sheet "enge Kurve"; 60 m keeps the
 *                                   inner side ground clear of the neighbouring straights)
 *
 *   Cross-section like SEG_01: asphalt ±4.45 m, plastic barriers ±3.58 m, fence ±4.2 m,
 *   chevron boards on the outer fence.
 *   Obstacle rows every 10 m from z = 5 (calm types: jump, slide, lane change – no double jumps
 *   while the camera swings through the bend), bottles on all lanes.
 *   Roadside bays outside only (single ground props inside).
 *   Backdrop: buildings outside, on the inner side only every second block; cranes outside only.
 *   No dynamic event sockets (moving hazards stay on straight modules).
 */
export const CURVES = {
  standard: { length: 40, angleDegrees: 45 },
  quarter: { length: 60, angleDegrees: 90 },
  rowSpacing: 10,
  firstRow: 5,
  collectibleSpacing: 2,
  /** Chevron boards on the outer fence (visual, see curveGreybox.ts). */
  chevronSpacing: 10,
  /** Inner-side backdrop buildings need this much room between them and the arc centre (m). */
  minInnerBackdropRadius: 15,
} as const;

const CALM_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const FACE_RIGHT = 90 * DEG_TO_RAD;
const FACE_LEFT = -90 * DEG_TO_RAD;

interface CurveSpec {
  readonly id: string;
  readonly kind: typeof SegmentKind.CurveLeft | typeof SegmentKind.CurveRight;
  readonly displayName: string;
  readonly length: number;
  /** Signed turn (radians): − left, + right. */
  readonly angle: number;
  readonly weight: number;
  readonly minDistance: number;
  readonly maxExitHeadingDegrees: number;
}

function curveExit(length: number, angle: number): ConnectorDefinition {
  return {
    id: 'CONNECTOR_OUT',
    role: ConnectorRole.Out,
    profile: ConnectorProfile.Ground3Lane,
    transform: arcExitPose(length, angle),
    allowedNextProfiles: [ConnectorProfile.Ground3Lane],
  };
}

/**
 * Site dressing per 20 m block like moduleSides (same socket ids), adapted to the bend:
 * roadside bays only on the outer side (on the inner side the rigid 10 m bays would converge
 * into each other and into the neighbouring straights – single ground props there), no cranes
 * on the inner side, inner backdrop buildings only every second block, fence banners on the
 * outer side moved in by the chord's sagitta so the flat panel never pokes behind the bent fence.
 */
function curveSides(length: number, angle: number): ModuleSides {
  const L = SITE_LAYOUT;
  const shape = { length, curve: { angle } };
  const innerSign = Math.sign(angle);
  const innerBackdropRadius = arcRadius(length, angle) - BACKDROP_SOCKET.x;
  const sides: ModuleSides = { decorationSockets: [], advertisingSlots: [], dynamicEventSockets: [] };
  for (let block = 0; block * L.blockLength < length - 1e-6; block++) {
    const o = block * L.blockLength;
    const s = block === 0 ? '' : `_${block + 1}`;
    const mid = o + L.blockLength / 2;
    for (const [side, sign, yaw] of [['L', -1, FACE_RIGHT], ['R', 1, FACE_LEFT]] as const) {
      const sideName = side === 'L' ? 'LEFT' : 'RIGHT';
      const inner = sign === innerSign;
      const sockets: DecorationSocket[] = nearSideSockets(sideName, s, o, { roadside: !inner });
      if (!inner || (block % 2 === 0 && innerBackdropRadius >= CURVES.minInnerBackdropRadius)) {
        sockets.push(backdropSocket(`SOCKET_DECO_${side}_04${s}`, sideName, sign * BACKDROP_SOCKET.x, mid, yaw));
      }
      // cranes: the jib (local +x) points across the track – outside the bend only
      if (!inner) sockets.push(craneSocket(`SOCKET_DECO_${side}_05${s}`, sideName, sign * CRANE_SOCKET.x, mid, side === 'L' ? 0 : Math.PI));
      sides.decorationSockets.push(...sockets);

      const lateral = L.fenceX - L.adSurfaceOffset;
      sides.advertisingSlots.push({
        id: `AD_FENCE_${sideName}${s}`,
        slotType: side === 'L' ? AdSlotType.FenceLeft : AdSlotType.FenceRight,
        position: { x: sign * (lateral - outerChordInset(shape, sign * lateral, L.adSize.width)), y: L.adHeight, z: mid },
        yaw,
        width: L.adSize.width,
        height: L.adSize.height,
      });
    }
  }
  return sides;
}

function createCurve(spec: CurveSpec): SegmentDefinition {
  const rows = Array.from({ length: Math.floor((spec.length - CURVES.firstRow) / CURVES.rowSpacing) + 1 }, (_, i) => CURVES.firstRow + i * CURVES.rowSpacing).filter(
    (z) => z < spec.length,
  );
  const rowId = (z: number) => `R${String(z).padStart(2, '0')}`;
  return {
    id: spec.id,
    kind: spec.kind,
    displayName: spec.displayName,
    length: spec.length,
    width: WORLD_CONFIG.trackWidth,
    // Greybox arc road until the Blender modules exist (manifest: curveGreybox.ts).
    visualAssetId: spec.id,
    curve: { angle: spec.angle },

    entry: entryConnector(),
    exits: [curveExit(spec.length, spec.angle)],
    lanes: standardLanes(WORLD_CONFIG),

    obstacleSpawnPoints: rows.flatMap((z) => obstacleRow(WORLD_CONFIG, rowId(z), z, CALM_TYPES)),
    obstacleRowLayouts: { sparse: rows.filter((_, i) => i % 2 === 1).map(rowId), dense: rows.map(rowId) },

    collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, spec.length - 1, CURVES.collectibleSpacing, COLLECTIBLE_FLOAT_HEIGHT),

    ...curveSides(spec.length, spec.angle),

    generation: {
      weight: spec.weight,
      minDistance: spec.minDistance,
      // the runner (and camera) is still turning right after a junction
      minSegmentsAfterJunction: 1,
      maxExitHeadingDegrees: spec.maxExitHeadingDegrees,
      family: 'CURVE',
      // 'bus-road': the ructec-Bulli drives curves (obstacles of a curve inside the ride are cleared, BusManager)
      tags: ['curve', 'ground', 'bus-road'],
    },
  };
}

/** Pick weights per module (both directions alike). Tune together with junction and straight weights. */
const WEIGHT = { standard: 0.16, quarter: 0.08 } as const;
const MIN_DISTANCE = { standard: 100, quarter: 250 } as const;
/**
 * 45° curves only bend back towards the start direction once the track has left it (zig-zag,
 * never two 45° curves to the same side in sequence): the track stays near the start axis,
 * where every junction type can still appear. 90° curves may lead fully sideways.
 */
const MAX_EXIT_HEADING = { standard: 45, quarter: 90 } as const;

export const SEG_CURVE_LEFT_45_40M = createCurve({
  id: 'SEG_CURVE_LEFT_45_40M',
  kind: SegmentKind.CurveLeft,
  displayName: 'Linkskurve 45° 40 m',
  length: CURVES.standard.length,
  angle: -CURVES.standard.angleDegrees * DEG_TO_RAD,
  weight: WEIGHT.standard,
  minDistance: MIN_DISTANCE.standard,
  maxExitHeadingDegrees: MAX_EXIT_HEADING.standard,
});

export const SEG_CURVE_RIGHT_45_40M = createCurve({
  id: 'SEG_CURVE_RIGHT_45_40M',
  kind: SegmentKind.CurveRight,
  displayName: 'Rechtskurve 45° 40 m',
  length: CURVES.standard.length,
  angle: CURVES.standard.angleDegrees * DEG_TO_RAD,
  weight: WEIGHT.standard,
  minDistance: MIN_DISTANCE.standard,
  maxExitHeadingDegrees: MAX_EXIT_HEADING.standard,
});

export const SEG_CURVE_LEFT_90_60M = createCurve({
  id: 'SEG_CURVE_LEFT_90_60M',
  kind: SegmentKind.CurveLeft,
  displayName: 'Linkskurve 90° 60 m',
  length: CURVES.quarter.length,
  angle: -CURVES.quarter.angleDegrees * DEG_TO_RAD,
  weight: WEIGHT.quarter,
  minDistance: MIN_DISTANCE.quarter,
  maxExitHeadingDegrees: MAX_EXIT_HEADING.quarter,
});

export const SEG_CURVE_RIGHT_90_60M = createCurve({
  id: 'SEG_CURVE_RIGHT_90_60M',
  kind: SegmentKind.CurveRight,
  displayName: 'Rechtskurve 90° 60 m',
  length: CURVES.quarter.length,
  angle: CURVES.quarter.angleDegrees * DEG_TO_RAD,
  weight: WEIGHT.quarter,
  minDistance: MIN_DISTANCE.quarter,
  maxExitHeadingDegrees: MAX_EXIT_HEADING.quarter,
});

export const CURVE_SEGMENTS: readonly SegmentDefinition[] = [SEG_CURVE_LEFT_45_40M, SEG_CURVE_RIGHT_45_40M, SEG_CURVE_LEFT_90_60M, SEG_CURVE_RIGHT_90_60M];
